import { Divider, Stack, Typography } from '@mui/material';
import { useForm } from 'react-typed-form';
import { useParams } from 'react-router';
import PublishingSection from '../../components/PublishingSection';
import { UnsavedChangesBlocker } from '../../components';
import { usePublishing } from '../../hooks';
import { Api, useApiCallable } from '../../api';
import {
  FieldMediaUpload,
  FieldNumber,
  FieldText,
  FieldTextRequired,
} from '../../form';

type Props = {
  existing?: Api.WeeklyTask.V_DetailAsAdmin;
};

export default function WeeklyTaskForm({ existing }: Props) {
  const callApi = useApiCallable();
  const { weekId } = useParams();

  const publishing = usePublishing(
    {
      contentType: 'Weekly Task',
      apiPath: 'weekly-tasks',
      existingId: existing?.id,
    },
    () => form.handleSubmit(),
  );

  const initialValues = {
    title: existing?.title ?? '',
    position: existing?.position ?? 1,
    resourceTitle: existing?.resourceTitle ?? '',
    description: existing?.description ?? '',
    week: `/program-weeks/${weekId}`,
    image: existing?.image?.['@id'] ?? null,
  };

  const form = useForm<{
    title: string;
    position: number | null;
    resourceTitle: string;
    description: string | null;
    week: string;
    image: string | null;
  }>({
    defaultValues: initialValues,
    onSubmit: async (values, form) => {
      const { setLoading, addSubmitError } = form;
      let response;

      if (!existing) {
        setLoading(true);
        response = await callApi(
          `/weekly-tasks`,
          {
            jsonBody: values,
            method: 'POST',
          },
          {
            addSubmitError,
          },
        );
      } else {
        response = await callApi(
          `/weekly-tasks/${existing.id}`,
          {
            method: 'PUT',
            jsonBody: values,
          },
          {
            addSubmitError,
          },
        );
      }

      setLoading(false);

      if (!response) return;

      await publishing.handleSubmitSuccess(response, !!existing);
    },
  });

  return (
    <Stack gap={3}>
      <Stack gap={3} maxWidth={600}>
        <FieldTextRequired field={form.getField('title')} label="Task" />
        <FieldNumber
          field={form.getField('position')}
          label="Position Number"
          helperText="e.g. 1 for the first task in the list"
          required
        />
        <Divider />
        <Stack>
          <Typography variant="h6">Resource</Typography>
          <Typography variant="body2" color="text.secondary">
            The resource is linked to the task and can be provided as text, an
            image/graphic, or both.
          </Typography>
        </Stack>
        <FieldTextRequired
          field={form.getField('resourceTitle')}
          label="Resource Title"
        />
        <FieldText
          field={form.getField('description')}
          label="Description"
          multiline
          minRows={4}
        />
        <FieldMediaUpload
          field={form.getField('image')}
          label="Image"
          acceptedTypes={['IMAGE_TYPE']}
          helperText="3:2 aspect ratio"
          aspectRatio="3/2"
          existingAsset={existing?.image}
        />
      </Stack>
      <PublishingSection
        existing={existing}
        isLoading={form.isLoading}
        contentType="Weekly Task"
        apiPath="weekly-tasks"
        publishing={publishing}
      />

      <UnsavedChangesBlocker
        initialValues={initialValues}
        currentValues={form.values}
        isSubmittingRef={publishing.isSubmittingRef}
      />
    </Stack>
  );
}
